import PremiumBenefitCategory, {
  PointDistribution,
  PREMIUM_BENEFIT_CONFIG,
} from 'app/frontend/types/custom/PremiumBenefitCategory'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Minus, Plus } from 'lucide-react'
import PremiumBenefitBar from './PremiumBenefitBar'

interface Props {
  category: PremiumBenefitCategory
  previousPoints: number
  pointsSpent: PointDistribution
  setPointsSpent: (value: number) => void
  className: string
}

const PremiumBenefitBlock = ({
  category,
  pointsSpent,
  previousPoints,
  setPointsSpent,
  className,
}: Props) => {
  const { t } = useTranslation()

  const currentLevel = Math.min(
    PREMIUM_BENEFIT_CONFIG[category].max,
    PREMIUM_BENEFIT_CONFIG[category].min +
      pointsSpent.total * PREMIUM_BENEFIT_CONFIG[category].step,
  )

  const steps =
    (PREMIUM_BENEFIT_CONFIG[category].max -
      PREMIUM_BENEFIT_CONFIG[category].start -
      PREMIUM_BENEFIT_CONFIG[category].step) /
    PREMIUM_BENEFIT_CONFIG[category].step

  const updatePoints = (value: number) => {
    if (value < 0) return

    setPointsSpent(value)
  }

  return (
    <div className={`${className} rounded-md py-2 px-3`}>
      <div className='flex items-center justify-between gap-2'>
        <div>
          <h4>
            <span>{t(`premium.benefits.${category}.label`)}</span>
          </h4>
          <p>{t(`premium.benefits.${category}.description`)}</p>
        </div>
        <span className='text-lg font-bold bg-accent py-2 px-3 rounded-md w-14 text-center'>
          {category === 'refresh_factor' ? '×' : ''}
          {currentLevel}
        </span>
      </div>

      <div className='flex gap-2 items-center'>
        <Button
          variant='outline'
          size='sm'
          className='rounded-lg h-8'
          disabled={pointsSpent.own === 0}
          onClick={() => updatePoints(pointsSpent.own - 1)}
        >
          <Minus size={16} />
        </Button>

        <PremiumBenefitBar
          steps={steps}
          pointsSpent={pointsSpent}
          previousPoints={previousPoints}
          onClick={updatePoints}
        />

        <Button
          size='sm'
          className='rounded-lg h-8'
          disabled={pointsSpent.own + pointsSpent.otherOrLegacy >= steps}
          onClick={() => updatePoints(pointsSpent.own + 1)}
          data-testid={`${category}-add-points`}
        >
          <Plus size={16} />
        </Button>
      </div>
    </div>
  )
}

export default PremiumBenefitBlock
